import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export type PopoverRef = {
  open: () => void;
  close: () => void;
};

export function Popover({
  children,
  handler,
  offset = { x: 0, y: 20 },
  ref,
}: {
  children: React.ReactNode;
  handler: React.ReactNode;
  offset?: { x: number; y: number };
  ref?: React.RefObject<PopoverRef | null>;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
  }));

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition({
      top: rect.top + offset.y,
      right: window.innerWidth - rect.right + offset.x,
    });
  }, [offset.x, offset.y]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
      <button ref={buttonRef} className="cursor-pointer" onClick={handleClick}>
        {handler}
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-100"
            style={{ top: position.top, right: position.right }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
